// services/RouteService.js
import axios from 'axios';
import { ROUTE_CONFIG } from '../config/routeConfig';
import { validateRouteData, validateAssignmentData } from '../utils/routeValidation';

export class RouteService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.client = axios.create({
      baseURL: baseUrl,
      timeout: ROUTE_CONFIG.API.TIMEOUT,
    });
    
    // Add request/response interceptors
    this.setupInterceptors();
  }
  
  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Response interceptor with retry logic
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;
        
        if (!config || !config.retry) {
          config.retry = 0;
        }
        
        if (config.retry < ROUTE_CONFIG.API.RETRY_ATTEMPTS) {
          config.retry++;
          
          await new Promise(resolve => 
            setTimeout(resolve, ROUTE_CONFIG.API.RETRY_DELAY * config.retry)
          );
          
          return this.client(config);
        }
        
        return Promise.reject(error);
      }
    );
  }
  
  /**
   * Fetch weekly routes for a specific date
   */
  async fetchWeeklyRoutes(date) {
    try {
      const formattedDate = date.toISOString().split('T')[0];
      const response = await this.client.get('/api/hmi/genericGetRequest', {
        params: {
          type: 'weeklyRoutesByDate',
          date: formattedDate
        }
      });
      console.log('69...got weekly routes', response)
      const validation = validateRouteData(response.data?.data?.routes);
      console.log('validation', validation)
      if (!validation.isValid) {
        throw new Error(`Invalid route data: ${validation.errors.join(', ')}`);
      }
      
      return this.processRouteResponse(response.data);
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch routes');
    }
  }
  
  /**
   * Assign a site to a crew
   */
  async assignSiteToCrew(assignmentData) {
    try {
      const validation = validateAssignmentData(assignmentData);
      if (!validation.isValid) {
        throw new Error(`Invalid assignment data: ${validation.errors.join(', ')}`);
      }
      
      const response = await this.client.post('/api/hmi/genericPostRequest', assignmentData, {
        params: { type: 'assignSiteToCrew' }
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to assign site to crew');
    }
  }
  
  /**
   * Unassign a site from a crew
   */
  async unassignSiteFromCrew(unassignmentData) {
    try {
      const response = await this.client.post('/api/hmi/genericPostRequest', unassignmentData, {
        params: { type: 'unassignSiteFromCrew' }
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to unassign site from crew');
    }
  }
  
  /**
   * Get nearest site details
   */
  async getNearestSiteDetails(site) {
    try {
      const response = await this.client.post('/api/hmi/getNearestSiteDetails', site);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to get site details');
    }
  }
  
  /**
   * Optimize route for given sites
   */
  async optimizeRoute(sites, existingRoute = null) {
    try {
      console.log('🚗 Optimizing route for', sites.length, 'sites');
      
      // Prepare the request data
      const requestData = {
        stops: sites.map((site, index) => ({
          siteId: site._id || site.siteId,
          name: site.name,
          latitude: site.latitude || site.location?.coordinates?.[1],
          longitude: site.longitude || site.location?.coordinates?.[0],
          estimatedHours: site.estimatedHours || 1,
          order: site.order || index + 1,
          isNew: site.isUnassigned || false
        }))
      };
      
      // Include crew ID from the existing route if available
      if (existingRoute && existingRoute.crewId) {
        requestData.crewId = existingRoute.crewId;
        console.log('🚗 Including crewId in optimization request:', existingRoute.crewId);
      }
      
      if (existingRoute) {
        requestData.existingRoute = existingRoute;
      }
      
      const response = await this.client.post('/api/hmi/routeOptimizationRequest', requestData);
      console.log('🚗 Route optimization response:', response.data);
      return response.data;
    } catch (error) {
      console.error('🚗 Route optimization failed:', error);
      throw this.handleError(error, 'Failed to optimize route');
    }
  }
  /**
   * Get future visits for a site
   */
  async getFutureVisits(siteId) {
    try {
      const response = await this.client.get('/api/hmi/genericGetRequest', {
        params: {
          type: 'futureVisits',
          siteId
        }
      });
      
      return response.data?.data || [];
    } catch (error) {
      throw this.handleError(error, 'Failed to get future visits');
    }
  }
  
  /**
   * Process route response data
   */
  processRouteResponse(data) {
    if (!data?.data?.routes) {
      throw new Error('Invalid route response format');
    }
    
    return {
      routes: data.data.routes.routes || [],
      sites: data.data.routes.sites || [],
      futureVisits: data.data.futureVisits || [],
      weekStart: data.data.routes.weekStart,
      weekEnd: data.data.routes.weekEnd,
      rawRoutes: data.data.routes.rawRoutes || []
    };
  }
  
  /**
   * Handle API errors consistently
   */
  handleError(error, defaultMessage) {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.message || defaultMessage;
      const status = error.response.status;
      
      return new RouteServiceError(message, status, error);
    } else if (error.request) {
      // Request made but no response
      return new RouteServiceError('Network error. Please check your connection.', 0, error);
    } else {
      // Something else happened
      return new RouteServiceError(error.message || defaultMessage, 0, error);
    }
  }
}

/**
 * Custom error class for route service
 */
export class RouteServiceError extends Error {
  constructor(message, status, originalError) {
    super(message);
    this.name = 'RouteServiceError';
    this.status = status;
    this.originalError = originalError;
  }
}
